'use client';

import React, {
  cloneElement,
  useId,
  useState,
  ChangeEvent,
  ReactElement,
  ReactNode,
  useRef,
} from 'react';
import classNames from 'classnames';

import { Checkbox } from '../Checkbox';
import { Error } from '../Error';
import { Helper } from '../Helper';

import './css/DateFieldset.scss';

export interface DateFieldsetProps {
  disabled?: boolean;
  error?: string | ReactElement;
  hasShowHide?: boolean;
  isShowHideChecked?: boolean;
  customShowHideLabel?: string;
  helper?: string;
  isDob?: boolean;
  isErrorAnnounced?: boolean;
  legend: string;
  monthLabel?: string;
  dayLabel?: string;
  yearLabel?: string;
  renderDayInput?: ReactElement<HTMLInputElement>;
  renderMonthInput?: ReactElement<HTMLInputElement>;
  renderYearInput?: ReactElement<HTMLInputElement>;
  required?: boolean;
}

export const DateFieldset = (props: DateFieldsetProps) => {
  const uid = useId();
  const {
    disabled,
    error,
    hasShowHide,
    isShowHideChecked = false,
    customShowHideLabel,
    helper,
    isDob = false,
    isErrorAnnounced = true,
    legend,
    renderDayInput,
    renderMonthInput,
    renderYearInput,
    required,
    monthLabel = 'Month',
    dayLabel = 'Day',
    yearLabel = 'Year',
  } = props;

  const [isShowDateChecked, setIsShowDateChecked] = useState(isShowHideChecked);

  let isCustomError = false;

  /**
   * Store custom error ids for each input
   */
  const customErrorIds = {
    month: '',
    day: '',
    year: '',
  };

  // counter for custom errors to create unique ids
  const customErrorIdCounter = useRef(0);

  // Process the error element to add unique ids to each custom error message
  const processError = (element: ReactElement): ReactNode => {
    if (!React.isValidElement(element)) {
      return element;
    }

    // Extract props from the element and create a new object to store the updated props
    const props = { ...(element.props as Record<string, unknown>) };
    const newProps = { ...props };

    // Determine if the error is custom via input-error attribute
    if (props['input-error']) {
      customErrorIdCounter.current += 1;

      // Set the flag to true only on the first iteration, used later to determine if the error is custom
      if (customErrorIdCounter.current === 1) isCustomError = true;

      const correspondingInput = props['input-error'] as
        | 'month'
        | 'day'
        | 'year';
      const errorId = `${uid}-${correspondingInput}-error-${customErrorIdCounter.current}`;

      /**
       * If the corresponding input's custom error ids string is empty, assign the error icon's id as the first mapped value to ensure that the error icon alt text is announced first before the error(s) by screen readers.
       */
      if (customErrorIds[correspondingInput] === '') {
        customErrorIds[correspondingInput] = `${uid}-error-icon`;
      }

      customErrorIds[correspondingInput] += ` ${errorId}`;
      customErrorIds[correspondingInput] =
        customErrorIds[correspondingInput].trim();
      // Update the id of the error element with the unique id
      newProps.id = errorId;
    }

    // Process the children of the element recursively
    const children = props.children as ReactNode | undefined;
    const newChildren = React.Children.toArray(children).map((child) =>
      React.isValidElement(child) ? processError(child) : child
    );

    // Clone element with the updated props and processed children
    return React.cloneElement(element, newProps, newChildren);
  };

  const processedError =
    typeof error === 'string' ? error : processError(error as ReactElement);

  const cloneInput = (
    el: ReactElement,
    fieldType: 'month' | 'day' | 'year',
    attributes: React.InputHTMLAttributes<HTMLInputElement>
  ) => {
    let input, id;
    if (el) {
      let errorIds = '';
      // If the error is not custom, set the error id to the container element of the error message
      if (error && !isCustomError) {
        errorIds = `${uid}-error`;
      }
      // If the error is custom, set the error id to the corresponding custom error ids string
      if (error && isCustomError) {
        errorIds = customErrorIds[`${fieldType}`];
      }

      const inputDescribedBy = `${helper ? `${uid}-helper` : ''} ${
        error ? `${errorIds}` : ''
      }`.trim();
      id =
        (el as ReactElement<HTMLInputElement>).props.id ||
        `${uid}-${fieldType}`;

      /**
       * Conditionally include the custom-error attribute on the input for styling purposes
       * When the error is not custom and is passed in (e.g., standard error string prop OR React element with no input-error attributes), all inputs will be in error and visually represented with a red border.
       * When a custom error is passed in (e.g., React element), only the corresponding inputs indicated via the input-error="month|day|year" attribute, will be in error and visually represented with a red border.
       */
      const additionalAttributes =
        isCustomError && errorIds.length > 0 ? { 'custom-error': '' } : {};

      input = cloneElement(el, {
        id,
        'inputMode': 'numeric',
        'type': hasShowHide && !isShowDateChecked ? 'password' : 'text',
        'aria-describedby': inputDescribedBy || null,
        'autoComplete': isDob ? `bday-${fieldType}` : null,
        required,
        ...attributes,
        ...additionalAttributes,
      });
    }

    return { input, id };
  };

  const { input: monthInput, id: monthInputId } = cloneInput(
    renderMonthInput as ReactElement,
    'month',
    {
      size: 2,
      maxLength: 2,
    }
  );
  const { input: dayInput, id: dayInputId } = cloneInput(
    renderDayInput as ReactElement,
    'day',
    {
      size: 2,
      maxLength: 2,
    }
  );
  const { input: yearInput, id: yearInputId } = cloneInput(
    renderYearInput as ReactElement,
    'year',
    {
      size: 4,
      maxLength: 4,
    }
  );

  const checkboxAriaControls = [monthInputId, dayInputId, yearInputId]
    .filter((c) => c) // remove any undefined values
    .join(' ')
    .trim();

  const handleShowHideChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsShowDateChecked(event.target.checked);
  };

  return (
    <fieldset
      className={classNames({
        'ps-date-fieldset': true,
        'ps-date-fieldset-error': !!error,
      })}
      disabled={disabled}
      {...(isCustomError ? { 'custom-errors': '' } : {})}
    >
      <legend>{legend}</legend>
      {helper && (
        <Helper
          id={`${uid}-helper`}
          disabled={disabled}
        >
          {helper}
        </Helper>
      )}
      <div className="ps-date-fieldset-wrapper">
        {monthInput && (
          <div>
            <label htmlFor={monthInputId}>{monthLabel}</label>
            <span className="ps-date-fieldset-input ps-date-fieldset-month">
              {monthInput}
            </span>
          </div>
        )}

        {dayInput && (
          <div>
            <label htmlFor={dayInputId}>{dayLabel}</label>
            <span className="ps-date-fieldset-input ps-date-fieldset-day">
              {dayInput}
            </span>
          </div>
        )}

        {yearInput && (
          <div>
            <label htmlFor={yearInputId}>{yearLabel}</label>
            <span className="ps-date-fieldset-input ps-date-fieldset-year">
              {yearInput}
            </span>
          </div>
        )}
      </div>

      {hasShowHide && (
        <Checkbox
          checked={isShowDateChecked}
          disabled={disabled}
          label={customShowHideLabel || `Show ${legend.toLowerCase()}`}
          aria-controls={checkboxAriaControls}
          onChange={handleShowHideChange}
        />
      )}

      {error && (
        <Error
          id={`${uid}-error`}
          isErrorAnnounced={isErrorAnnounced}
        >
          {processedError}
        </Error>
      )}
    </fieldset>
  );
};
